import type { GmRole } from "./capabilities.ts";

/**
 * Standing: who may act on whom (ADR-081's amendment of 2026-10-04). What an account may do is the union of its
 * roles' capabilities, a set and not a ladder; whom it may do it to needs an order, and this is the order. It is
 * read for moderation alone (ADR-056's amendment of 2026-10-04, ADR-068's note of the same day) and grants nothing.
 * The administrator's capabilities are computed from it (`capabilities.ts`, ADR-081's amendment of 2026-10-06), but
 * what an account may do stays the union of its roles' sets.
 */

/**
 * Each role's standing, as ADR-112's note "the standing table corrected" has it: a player and a Guide 0; support,
 * event, builder, designer and the Guide leader 1; moderator 2; the four staff leaders 3; administrator 4; lead 5.
 * Only the order is read, never a number.
 */
export const GM_STANDING: Readonly<Record<GmRole, number>> = Object.freeze({
  support: 1,
  moderator: 2,
  administrator: 4,
  event: 1,
  builder: 1,
  designer: 1,
  lead: 5,
  guide: 0,
  guide_lead: 1,
  support_lead: 3,
  event_lead: 3,
  builder_lead: 3,
  designer_lead: 3,
});

/**
 * An account's standing: the highest of its live roles'.
 *
 * @param roles The account's live roles, in any order, repeats allowed.
 * @returns The standing, 0 for an account holding no role.
 */
export function standingOf(roles: readonly GmRole[]): number {
  let standing = 0;
  for (const role of roles) {
    standing = Math.max(standing, GM_STANDING[role]);
  }
  return standing;
}

/**
 * Whether an account may moderate another: when its standing is higher, or when both hold the `lead` role (the
 * author's ruling of 2026-10-07, ADR-056's amendment of that date), the one exception to "lower standing only".
 * Equal standing is otherwise refused, so nobody else acts on a peer, nobody but a lead acts on a lead, and a player
 * acts on nobody. Only the `lead` role counts as a lead: an administrator never does. Whether the target is the
 * acting account itself is the caller's check (`own`), as is the action's capability; this answers only whom an
 * action may be aimed at.
 *
 * @param acting The live roles of the account that acts.
 * @param target The live roles of the account acted on.
 * @returns True when the acting account stands above the target, or both are leads.
 * @example
 * mayActOn(["moderator"], ["support"]);
 * // true
 * mayActOn(["administrator"], ["lead"]);
 * // false
 * mayActOn(["lead"], ["lead"]);
 * // true
 */
export function mayActOn(acting: readonly GmRole[], target: readonly GmRole[]): boolean {
  return standingOf(acting) > standingOf(target) || (acting.includes("lead") && target.includes("lead"));
}
